/**
 * The memo comparators behind Cell and BoardCard.
 *
 * A replay step re-derives every cell's props from a fresh state clone, so
 * reference checks alone would re-render all 18 cells (and the cards in them)
 * per event. The data involved is small plain JSON — the engine forbids
 * Map/Set/functions in state — which makes deep comparison far cheaper than
 * reconciling a cell subtree.
 *
 * Structural comparison covers arrays and plain objects. Any OTHER object is
 * compared by its own enumerable keys, which for most of them is nothing at all:
 * `Object.keys(new Set([1]))` is `[]`, so two different Sets look equal, and the
 * same trap catches Maps, Dates and class instances. So everything a memoized
 * component receives has to be plain data — derive a boolean or an array from a
 * Set before passing it, and keep React nodes out of memoized props.
 */
export function dataEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => dataEqual(item, b[index]));
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    Object.hasOwn(b, key)
    && dataEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}

/**
 * Compares every prop by value, except callbacks — those are expected to be
 * identity-stable dispatchers owned by the parent, so a changed function
 * identity is treated as a real change rather than silently ignored.
 */
export function propsEqualByValue<P extends object>(previous: P, next: P): boolean {
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]) as Set<keyof P>;
  for (const key of keys) {
    const a = previous[key];
    const b = next[key];
    if (typeof a === 'function' || typeof b === 'function') {
      if (a !== b) return false;
      continue;
    }
    if (!dataEqual(a, b)) return false;
  }
  return true;
}
